// 角色权限矩阵（前端体验层镜像）—— 与 apps/core-api 的 PERMISSION_MATRIX 严格对齐（最小权限）
// 注意：这里只用于按钮/入口的体验层显隐，真正的强制在后端（requireReadPermission / requireWritePermission
// / STATUS_FLOWS / canPrint）。前端隐藏不代表安全边界。后端矩阵快照可通过 GET /api/permissions 读取。

import type { Role, User } from './types';

// —— 统一身份角色（方案 1）——
// Integral 登录后 user.role 可能是统一身份角色（company_admin/engineer/finance/customer），
// 而不是旧 users 表的岗位角色。权限矩阵按岗位角色编写，故先经此映射展开，
// 再用既有矩阵判定 —— 与后端 apps/core-api/src/identity/integral-role-map.js 严格同源。
//
// 口径与后端保持一致：
//   engineer      = sales + production + quality + warehouse（完整工程业务，不含 admin/finance）
//   company_admin = admin
//   finance       = sales + production + quality + warehouse + finance
//   customer      = []（不进入 Integral 业务控制台）
const IDENTITY_ROLE_MAP: Record<string, Role[]> = {
  engineer: ['sales', 'production', 'quality', 'warehouse'],
  company_admin: ['admin'],
  finance: ['sales', 'production', 'quality', 'warehouse', 'finance'],
  customer: [],
};

// 业务操作员口径（与 core-api 的 BIZ_ROLES 严格同源）：受控清理入口
// （删库存流水 / 删维修版本）按「管理员 + BIZ_ROLES」放开，与后端路由判定一致。
export const BIZ_ROLES: Role[] = ['sales', 'production', 'quality', 'warehouse'];

/** 该用户的全部岗位角色（统一身份角色会被展开；旧账号角色原样保留）。 */
export function rolesOf(user: User | null | undefined): Role[] {
  const role = user?.role as string | undefined;
  if (!role) return [];
  const mapped = IDENTITY_ROLE_MAP[role];
  if (mapped) return [...mapped];
  return [role as Role];
}

/** 该用户是否具备某个岗位角色。 */
export function hasRole(user: User | null | undefined, role: Role): boolean {
  return rolesOf(user).includes(role);
}

export type PermissionModule =
  | 'customers' | 'suppliers' | 'items' | 'item-code-rules' | 'operations' | 'warehouses' | 'boms'
  | 'process-templates' | 'material-plans' | 'qc-templates' | 'qc-review'
  | 'stock/movements' | 'work-orders' | 'price-lists' | 'purchase-orders'
  | 'contracts' | 'quotations' | 'deliveries' | 'inspections' | 'returns' | 'customer-supplied-materials' | 'physical-assets' | 'arca-invoices' | 'payments' | 'attachments' | 'leads'
  | 'users';

export type PermissionOp = 'view' | 'create' | 'edit' | 'transition' | 'cancel' | 'print';

interface ModulePermission {
  view: Role[];
  create: Role[];
  edit: Role[];
  transition: Role[];
  cancel: Role[];
  print: Role[];
}

const m = (
  view: Role[],
  create: Role[],
  edit: Role[] = create,
  transition: Role[] = [],
  print: Role[] = [],
): ModulePermission => ({ view, create, edit, transition, cancel: ['admin'], print });

// 与后端逐模块 view/create/edit/transition/cancel/print 一一对应。
export const PERMISSION_MATRIX: Record<PermissionModule, ModulePermission> = {
  customers: m(['admin', 'sales', 'warehouse', 'production', 'finance', 'viewer'], ['sales'], ['admin']),
  items: m(['admin', 'sales', 'production', 'quality', 'warehouse', 'finance', 'viewer'], ['sales', 'production', 'warehouse'], ['admin']),
  suppliers: m(['admin', 'warehouse', 'production', 'finance', 'viewer'], ['warehouse'], ['admin']),
  'purchase-orders': m(['admin', 'warehouse', 'finance', 'viewer'], ['warehouse', 'finance'], ['admin']),
  'price-lists': m(['admin', 'sales', 'finance', 'viewer'], ['sales', 'finance'], ['sales', 'finance']),
  boms: m(['admin', 'sales', 'production', 'warehouse', 'finance', 'viewer'], ['production'], ['admin'], ['admin', 'sales', 'production', 'quality', 'warehouse']),
  // 工艺方案三层（P6-5）：生产建单/编辑/审批流转，质量·仓储·销售·只读可查看，编辑落管理员，与后端对齐。
  'process-templates': m(['admin', 'sales', 'production', 'quality', 'warehouse', 'viewer'], ['production'], ['production'], ['production']),
  'material-plans': m(['admin', 'sales', 'production', 'quality', 'warehouse', 'viewer'], ['production'], ['production'], ['production']),
  // 质检模板 / 量化公差主数据（接管）：质量创建/编辑/复核批准，生产·仓库·销售·查看只读，财务不涉及。与后端 PERMISSION_MATRIX 对齐。
  'qc-templates': m(['admin', 'sales', 'production', 'quality', 'warehouse', 'viewer'], ['quality'], ['quality']),
  // 决策二：工序「是否需检」的三态审核工作台。质量可判定（create=edit=quality），
  // 生产/仓库/销售/查看只读——让他们看到「还差几道没审」有助于推进，但不给他们判定权。与后端 PERMISSION_MATRIX['qc-review'] 对齐。
  'qc-review': m(['admin', 'production', 'quality', 'warehouse', 'viewer'], ['quality'], ['quality']),
  'work-orders': m(['admin', 'sales', 'production', 'warehouse', 'finance', 'viewer'], ['production'], ['production']),
  operations: m(['admin', 'sales', 'production', 'warehouse', 'finance', 'viewer'], ['production'], ['admin']),
  'item-code-rules': m(['admin', 'production'], ['production'], ['admin']),
  warehouses: m(['admin', 'sales', 'production', 'quality', 'warehouse', 'finance', 'viewer'], ['warehouse'], ['admin']),
  'stock/movements': m(['admin', 'production', 'warehouse', 'viewer'], ['warehouse', 'production', 'sales', 'finance'], []),
  quotations: m(['admin', 'sales', 'finance', 'viewer'], ['sales'], ['sales'], ['sales'], ['admin', 'sales', 'finance']),
  contracts: m(['admin', 'sales', 'warehouse', 'production', 'quality', 'finance', 'viewer'], ['sales'], ['sales'], ['sales'], ['admin', 'sales', 'finance']),
  deliveries: m(['admin', 'sales', 'production', 'quality', 'warehouse', 'finance', 'viewer'], ['sales', 'warehouse'], ['sales', 'warehouse'], ['sales', 'warehouse'], ['admin', 'sales', 'warehouse']),
  inspections: m(['admin', 'sales', 'production', 'quality', 'warehouse', 'finance', 'viewer'], ['quality'], ['admin'], [], ['admin', 'quality', 'sales', 'warehouse']),
  returns: m(['admin', 'sales', 'warehouse', 'viewer'], ['sales', 'warehouse'], ['admin']),
  // 客户来料（维修/加工）：查看含质检；建单/交还/状态流转为 仓储·生产·销售；编辑仅管理员。
  'customer-supplied-materials': m(['admin', 'sales', 'production', 'quality', 'warehouse', 'viewer'], ['warehouse', 'production', 'sales'], ['admin'], ['warehouse', 'production', 'sales']),
  // 工件主档 + 维修版本（阶段6）：查看含质检/只读；建档/开版本/状态流转为 生产·质检·仓储；编辑仅管理员。与后端 PERMISSION_MATRIX 对齐。
  'physical-assets': m(['admin', 'sales', 'production', 'quality', 'warehouse', 'viewer'], ['production', 'quality', 'warehouse'], ['admin'], ['production', 'quality', 'warehouse']),
  leads: m(['admin', 'sales', 'viewer'], ['sales'], ['sales']),
  'arca-invoices': m(['admin', 'finance'], ['finance'], ['admin']),
  payments: m(['admin', 'finance'], ['finance'], ['admin']),
  // 附件基础模型：业务操作员 + 财务可上传与查看；编辑/替换仅管理员。
  // 与后端 PERMISSION_MATRIX.attachments 对齐；不含 viewer（附件含客户与税务原件）。
  attachments: m(['admin', 'finance', 'sales', 'production', 'quality', 'warehouse'], ['finance', 'sales', 'production', 'quality', 'warehouse'], ['admin']),
  users: m(['admin'], ['admin'], ['admin']),
};

// 统一权限判定：admin 短路放行（与后端 requireReadPermission/requireWritePermission 一致）。
export function can(user: User | null | undefined, module: PermissionModule, op: PermissionOp): boolean {
  const roles = rolesOf(user);
  if (!roles.length) return false;
  if (roles.includes('admin')) return true;
  const allowed = PERMISSION_MATRIX[module]?.[op] ?? [];
  return roles.some((r) => allowed.includes(r));
}

// 草稿可编辑 = 角色具备 edit 权限 且（管理员或本人创建）。后端会二次校验，前端仅作体验层。
export function canEditOwnDraft(
  user: User | null | undefined,
  module: PermissionModule,
  record: { created_by?: string | null },
): boolean {
  if (!user) return false;
  if (!can(user, module, 'edit')) return false;
  if (hasRole(user, 'admin')) return true;
  return !!record.created_by && record.created_by === user.id;
}

// 审计读取（与后端路由内联强制保持一致）。全量审计：管理员 + 财务（财务由后端强制收窄为财务相关）。
export function canViewFullAudit(user: User | null | undefined): boolean {
  return hasRole(user, 'admin') || hasRole(user, 'finance');
}
export function canViewRecordAudit(user: User | null | undefined): boolean {
  if (!user) return false;
  const roles = rolesOf(user);
  return roles.includes('admin') || (['sales', 'warehouse', 'finance'] as Role[]).some((r) => roles.includes(r));
}

// 采购成本字段级权限：仅 admin/finance 可见可改。与 core-api canManagePurchaseCost 同源。
export function canManagePurchaseCost(user: User | null | undefined): boolean {
  return hasRole(user, 'admin') || hasRole(user, 'finance');
}
